import { useCallback, useRef, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { ScheduledJobProto } from "build-ts/schedule";
import { populateArtifact } from "services/DataService";
import showToast from "hooks/useCustomToast";
import {
  getArtifactHeaderId,
  getArtifactType,
  buildWorkflowRunViewState,
  workflowRunNeedsHydration,
} from "../utils/artifactUtils";
import { TAB_ID } from "../dataframe/utils/dataFrameCodeUtils";
import { SelectedArtifactItem } from "modules/interfaces";

interface UseArtifactPanelOptions {
  /** Called after populate to cache the hydrated artifact back into the registry. */
  updateArtifact: (next: ArtifactProto) => void;
  /** Called whenever the panel first opens (e.g. collapse the navbar). */
  onOpen?: () => void;
}

/**
 * Owns all artifact panel state and provides stable, typed open-handlers for each
 * artifact type.
 * Stale-closure safety: all async handlers read `selectedArtifactsRef.current`
 * (kept in sync via the custom `setSelectedArtifacts` wrapper) instead of capturing
 * `selectedArtifacts` in the `useCallback` closure.  This guarantees that the index
 * found after an `await` reflects the latest panel state, not the state at call time.
 */
export function useArtifactPanel({ updateArtifact, onOpen }: UseArtifactPanelOptions) {
  // ── Panel visibility ────────────────────────────────────────────────────────
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingArtifact, setIsLoadingArtifact] = useState(false);

  // ── Selected artifacts (tab list) ──────────────────────────────────────────
  // The ref mirrors state so async callbacks always read the latest value without
  // needing to be recreated on every render.
  const selectedArtifactsRef = useRef<SelectedArtifactItem[]>([]);
  const [selectedArtifacts, setSelectedArtifactsInternal] = useState<SelectedArtifactItem[]>([]);

  /**
   * Functional-updater-only wrapper around setSelectedArtifactsInternal.
   * Every mutation goes through here so the ref stays in sync with state:
   * 1. Calls the updater with the current list to derive the next list.
   * 2. Writes the result to selectedArtifactsRef.current immediately (before React
   *    re-renders), so async callbacks that read the ref see the new value right away.
   * 3. Returns the result to React so state is updated on the next render.
   *
   * Deps: none — wraps only the stable React internal setter.
   */
  const setSelectedArtifacts = useCallback((updater: (prev: SelectedArtifactItem[]) => SelectedArtifactItem[]) => {
    setSelectedArtifactsInternal(prev => {
      const next = updater(prev);
      selectedArtifactsRef.current = next;
      return next;
    });
  }, []);

  // ── Activation signal ──────────────────────────────────────────────────────
  // Signal to ArtifactPanel to focus an existing tab without moving/adding it.
  const [activateArtifact, setActivateArtifact] = useState<{ orderIndex: number; tick: number } | undefined>(undefined);

  // ── Monotonically-increasing tab order counter ─────────────────────────────
  const orderCounter = useRef(0);

  // ── Pending group tracker for ANALYSIS_OUTPUT ──────────────────────────────
  // Multiple onArtifactClick calls fired synchronously from the same forEach
  // share one groupId; cleared via setTimeout so the next independent click
  // starts a fresh group.
  const pendingAnalysisGroupRef = useRef<{
    groupId: string;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  // ── Stable ref for onOpen callback (avoids re-creating handlers on prop change) ──
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Make the panel visible and fire the onOpen side-effect (e.g. collapse the navbar).
   * Called at the start of every open-handler so the user sees the panel immediately.
   *
   * Deps: none — reads only the stable ref, no state or props captured directly.
   */
  const openPanel = useCallback(() => {
    setIsVisible(true);
    onOpenRef.current?.();
  }, []);

  /**
   * Locate an item in the current panel list by artifact type + artifact id.
   * Reads `selectedArtifactsRef.current` so it is always stale-closure-safe.
   * Returns the index, or -1 if not found / artifactId is undefined.
   */
  const findInPanel = (type: ArtifactType, artifactId: string | undefined): number => {
    if (!artifactId) return -1;
    return selectedArtifactsRef.current.findIndex(
      item =>
        !!item.artifact && getArtifactType(item.artifact) === type && getArtifactHeaderId(item.artifact) === artifactId
    );
  };

  /**
   * Manages the panel's tab list as a logical "upsert" (insert-or-update):
   *
   * UPDATE path — artifact already has an open tab (matched by type + header id):
   *   The existing entry is replaced in-place with `newItem` (preserving its position
   *   in the tab strip). The tab's `orderIndex` never changes, so the tab does not move.
   *   The existing `orderIndex` is returned so the caller can focus that tab via
   *   `setActivateArtifact`.
   *
   * INSERT path — no matching tab exists:
   *   A new entry is appended with the next monotonically increasing `orderIndex`
   *   (tabs are sorted by this value, so newer tabs always appear to the right).
   *   The new `orderIndex` is returned so the caller can focus the newly added tab
   *   via `setActivateArtifact`.
   *
   * In both cases the caller MUST fire `setActivateArtifact` with the returned index.
   * Activation is always explicit — this function only manages the list, not the focus.
   *
   * Deps: setSelectedArtifacts — the only external call made inside.
   * findInPanel and orderCounter are plain helpers/refs with no deps of their own.
   */
  const upsertSelectedItem = useCallback(
    (newItem: Omit<SelectedArtifactItem, "orderIndex">, artifactId: string | undefined): number => {
      const current = selectedArtifactsRef.current;
      const existingIdx = findInPanel(getArtifactType(newItem.artifact!), artifactId);

      if (existingIdx !== -1) {
        // UPDATE: merge newItem into the existing entry without changing its position.
        const existingOrderIndex = current[existingIdx].orderIndex;
        setSelectedArtifacts(prev => prev.map((item, idx) => (idx === existingIdx ? { ...item, ...newItem } : item)));
        return existingOrderIndex;
      }

      // INSERT: append with the next order index so the tab appears rightmost.
      orderCounter.current++;
      const orderIndex = orderCounter.current;
      setSelectedArtifacts(prev => [...prev, { ...newItem, orderIndex }]);
      return orderIndex;
    },
    [setSelectedArtifacts]
  );

  /**
   * Fetches full artifact data via populateArtifact, caches it back into the
   * store registry via updateArtifact, and returns the hydrated artifact.
   * Returns null if the populate call fails or the type is unsupported.
   * Does NOT guard against already-hydrated artifacts — callers are responsible
   * for that check so they can take their own fast path.
   */
  const hydrateArtifact = useCallback(
    async (artifact: ArtifactProto, chatId?: string): Promise<ArtifactProto | null> => {
      const type = getArtifactType(artifact);
      if (type === ArtifactType.KG_DATAFRAME && artifact.kg_dataframe) {
        const request = ArtifactProto.create({
          artifact_type: ArtifactType.KG_DATAFRAME,
          kg_dataframe: artifact.kg_dataframe,
        });
        const response = await populateArtifact(request);
        if (response?.kg_dataframe) {
          const hydrated = { ...artifact, kg_dataframe: response.kg_dataframe };
          updateArtifact(hydrated);
          return hydrated;
        }
      } else if (type === ArtifactType.DATAFRAME_ANALYSIS && artifact.dataframe_analysis) {
        const request = ArtifactProto.create({
          artifact_type: ArtifactType.DATAFRAME_ANALYSIS,
          dataframe_analysis: artifact.dataframe_analysis,
          chat_id: artifact.chat_id || chatId,
        });
        const response = await populateArtifact(request);
        if (response?.dataframe_analysis) {
          const hydrated = { ...artifact, dataframe_analysis: response.dataframe_analysis };
          updateArtifact(hydrated);
          return hydrated;
        }
      } else if (type === ArtifactType.FILE_ARTIFACT && artifact.file_artifact) {
        const request = ArtifactProto.create({
          artifact_type: ArtifactType.FILE_ARTIFACT,
          file_artifact: artifact.file_artifact,
          chat_id: artifact.chat_id || chatId,
        });
        const response = await populateArtifact(request);
        if (response?.file_artifact) {
          const hydrated = { ...artifact, file_artifact: response.file_artifact };
          updateArtifact(hydrated);
          return hydrated;
        }
      } else if (type === ArtifactType.WORKFLOW_RUN && artifact.workflow_run) {
        const request = ArtifactProto.create({
          artifact_type: ArtifactType.WORKFLOW_RUN,
          workflow_run: artifact.workflow_run,
        });
        const response = await populateArtifact(request);
        if (response?.workflow_run) {
          const hydrated = { ...artifact, workflow_run: response.workflow_run };
          updateArtifact(hydrated);
          return hydrated;
        }
      }
      return null;
    },
    [updateArtifact]
  );

  // ── Public open-handlers ───────────────────────────────────────────────────

  /**
   * Open a KG_DATAFRAME artifact.
   * If the artifact already has column data: adds it to the panel immediately.
   * If not: shows the panel, fires populateArtifact to hydrate it, then upserts
   * the result and caches the hydrated artifact back into the store registry.
   *
   * Deps: updateArtifact (write hydrated artifact back to store),
   *       openPanel (show panel before async work),
   *       upsertSelectedItem (insert/update the panel tab).
   */
  const openKGDataFrame = useCallback(
    async (artifact: ArtifactProto) => {
      if (!artifact.kg_dataframe) {
        showToast("Missing kg_dataframe data", "", "error");
        return;
      }
      const artifactId = getArtifactHeaderId(artifact);
      const hasData = artifact.kg_dataframe.ordered_columns.some(col => col.data_value_json_array);
      openPanel();
      if (hasData) {
        const activatedOrderIndex = upsertSelectedItem(
          { artifact, kgDataFrameData: artifact.kg_dataframe, loading: false },
          artifactId
        );
        setActivateArtifact(prev => ({
          orderIndex: activatedOrderIndex,
          tick: (prev?.tick ?? 0) + 1,
        }));
        return;
      }
      setIsLoadingArtifact(true);
      // hydrateArtifact catches all network/parse errors internally and returns null —
      // no catch needed here; the finally block always resets the loading flag.
      try {
        const hydrated = await hydrateArtifact(artifact);
        if (hydrated && hydrated?.kg_dataframe) {
          // upsertSelectedItem reads selectedArtifactsRef.current — stale-closure-safe after await.
          const activatedOrderIndex = upsertSelectedItem(
            { artifact: hydrated, kgDataFrameData: hydrated.kg_dataframe, loading: false },
            artifactId
          );
          setActivateArtifact(prev => ({
            orderIndex: activatedOrderIndex,
            tick: (prev?.tick ?? 0) + 1,
          }));
        }
      } finally {
        setIsLoadingArtifact(false);
      }
    },
    [hydrateArtifact, openPanel, upsertSelectedItem]
  );

  /**
   * Open a DATAFRAME_ANALYSIS artifact.
   * If data is already present and charts are hydrated: adds to panel immediately.
   * Otherwise: fires populateArtifact (requires chatId for query context), then
   * upserts the hydrated result and caches it back into the store registry.
   * Preserves the active tab / selected index when no explicit view is provided.
   *
   * Deps: updateArtifact (write hydrated artifact back to store),
   *       openPanel (show panel before async work),
   *       upsertSelectedItem (insert/update the panel tab).
   */
  const openDataFrameAnalysis = useCallback(
    async (
      artifact: ArtifactProto,
      chatId: string | undefined,
      activeTab?: string,
      /**
       * Name of the sub-resource (pandas_df or chart) that triggered the open.
       * The index is resolved from this name in both paths:
       * - Already hydrated: resolved immediately from the artifact's arrays.
       * - Needs hydration: re-resolved after populate() so stale/missing indices
       *   (common on page reload where pandas_dataframes arrives empty) are never used.
       */
      subResourceName?: string
    ) => {
      if (!artifact.dataframe_analysis) {
        showToast("Missing dataframe_analysis data", "", "error");
        return;
      }

      const artifactId = getArtifactHeaderId(artifact);
      // subResourceName is the name of the pill to activate. Pass it through directly —
      // the component matches by name, so no index resolution or offset math is needed.
      const dataframeAnalysisView = { activeTab: activeTab ?? TAB_ID.results, selectedItemName: subResourceName ?? "" };

      const dataframes = artifact.dataframe_analysis.pandas_dataframes;
      // Only require dataframe hydration when there are actual dataframes to load.
      // An artifact with no pandas_dataframes (e.g. a pure docx report) should not
      // trigger populate just because hasData would be false for an empty array.
      const dataframesNeedHydration = dataframes.length > 0 && !dataframes.some(df => !!df.data_json);

      const charts =
        (artifact.dataframe_analysis as unknown as { charts?: Array<{ spec_json?: string }> } | undefined)?.charts ??
        [];
      const hasChartNeedingHydration = charts.some(chart => {
        const raw = chart.spec_json?.trim();
        if (!raw) return true;
        try {
          return (JSON.parse(raw) as Record<string, unknown>).data === undefined;
        } catch {
          return false;
        }
      });

      // Reports are hydrated when every entry already has pdf_bytes.
      // If pdf_bytes are present we skip populate — no need to re-fetch.
      const reports = artifact.dataframe_analysis.reports ?? [];
      const reportsNeedHydration = reports.length > 0 && reports.some(r => !r.pdf_bytes || r.pdf_bytes.length === 0);

      // Prefer the already-hydrated copy from the panel (which may have pdf_bytes / spec_json
      // / data_json) over the incoming message-chain artifact, which is a shallow copy.
      // This prevents redundant populate calls when the user re-clicks an already-open artifact.
      const existingPanelIdx = findInPanel(ArtifactType.DATAFRAME_ANALYSIS, artifactId);
      const panelArtifact =
        existingPanelIdx >= 0 ? (selectedArtifactsRef.current[existingPanelIdx]!.artifact ?? artifact) : artifact;

      // Re-evaluate hydration need against the panel artifact — if the panel copy is
      // already hydrated (has data_json / spec_json / pdf_bytes), shouldPopulate = false.
      const panelDfa = panelArtifact.dataframe_analysis;
      const panelDataframes = panelDfa?.pandas_dataframes ?? [];
      const panelCharts = panelDfa?.charts ?? [];
      const panelReports = panelDfa?.reports ?? [];

      const panelDataframesNeedHydration = panelDataframes.length > 0 && !panelDataframes.some(df => !!df.data_json);
      const panelChartsNeedHydration = panelCharts.some(chart => {
        const raw = chart.spec_json?.trim();
        if (!raw) return true;
        try {
          return (JSON.parse(raw) as Record<string, unknown>).data === undefined;
        } catch {
          return false;
        }
      });
      const panelReportsNeedHydration =
        panelReports.length > 0 && panelReports.some(r => !r.pdf_bytes || r.pdf_bytes.length === 0);

      const shouldPopulate =
        (panelDataframesNeedHydration || panelChartsNeedHydration || panelReportsNeedHydration) &&
        (dataframesNeedHydration || hasChartNeedingHydration || reportsNeedHydration);
      openPanel();
      if (!shouldPopulate) {
        const activatedOrderIndex = upsertSelectedItem(
          {
            artifact: panelArtifact,
            loading: false,
            dataframeAnalysisView,
          },
          artifactId
        );
        setActivateArtifact(prev => ({
          orderIndex: activatedOrderIndex,
          tick: (prev?.tick ?? 0) + 1,
        }));
        return;
      }
      if (!artifact.chat_id && !chatId) {
        showToast("Missing chat id for dataframe analysis hydration", "", "error");
        return;
      }
      setIsLoadingArtifact(true);
      // hydrateArtifact catches all network/parse errors internally and returns null —
      // no catch needed here; the finally block always resets the loading flag.
      try {
        const hydratedArtifact = await hydrateArtifact(artifact, chatId);
        // Fall back to the original artifact if hydration failed (network error etc.) so that
        // the view state (correct tab + pill) is still applied even without pdf_bytes.
        const artifactToShow = hydratedArtifact ?? artifact;
        // dataframeAnalysisView already carries selectedItemName = subResourceName.
        // Name-based matching is stable across hydration — no re-resolution needed.
        // upsertSelectedItem reads selectedArtifactsRef.current — stale-closure-safe after await.
        const activatedOrderIndex = upsertSelectedItem(
          {
            artifact: artifactToShow,
            loading: false,
            dataframeAnalysisView,
          },
          artifactId
        );
        setActivateArtifact(prev => ({
          orderIndex: activatedOrderIndex,
          tick: (prev?.tick ?? 0) + 1,
        }));
      } finally {
        setIsLoadingArtifact(false);
      }
    },
    [hydrateArtifact, openPanel, upsertSelectedItem]
  );

  /**
   * Open a SCENARIO_JOB artifact.
   * Re-clicks bring the tab to the front: the old entry is removed and re-appended
   * in a single state update so the panel always shows the latest version at the end.
   * openPanel() fires first so the user sees the panel before the list updates.
   *
   * Deps: openPanel (show panel immediately),
   *       setSelectedArtifacts (atomic remove + append in one update).
   */
  const openScenarioJob = useCallback(
    (artifact: ArtifactProto) => {
      if (!artifact.scenario_job) {
        showToast("Missing scenario_job data", "", "error");
        return;
      }
      // Open the panel immediately so the user sees something before the state update.
      openPanel();
      const incomingJobId = artifact.scenario_job.header?.id;

      orderCounter.current++;
      const orderIndex = orderCounter.current;
      // Duplicate detection runs inside the updater so it always operates on the
      // live `prev` state, not on a snapshot captured before the batch runs.
      //
      // Problem with pre-computing the index outside:
      //   Panel has [A(0), B(1), C(2)]. A step click fires openScenarioJob(A),
      //   openScenarioJob(B), openScenarioJob(C) synchronously. All three read the
      //   same ref ([A,B,C]) and capture dupIdx = 0, 1, 2 respectively. React batches
      //   the three updaters. Updater-A runs on [A,B,C] → [B,C,A] ✓. Updater-B then
      //   runs on [B,C,A] and filters index 1 (C, not B) → removes wrong item. Updater-C
      //   is equally wrong. Result: wrong items removed, some artifacts duplicated.
      //
      // With the index computed inside each updater from `prev`, every lookup is
      // correct regardless of what the previous updaters already modified.
      setSelectedArtifacts(prev => {
        const dupIdx = incomingJobId
          ? prev.findIndex(
              item =>
                getArtifactType(item.artifact!) === ArtifactType.SCENARIO_JOB &&
                item.artifact?.scenario_job?.header?.id === incomingJobId
            )
          : -1;
        const filtered = dupIdx !== -1 ? prev.filter((_, i) => i !== dupIdx) : prev;
        return [...filtered, { artifact, orderIndex, loading: false }];
      });
      setActivateArtifact(prev => ({
        orderIndex,
        tick: (prev?.tick ?? 0) + 1,
      }));
    },
    [openPanel, setSelectedArtifacts]
  );

  /**
   * Open an ANALYSIS_OUTPUT artifact.
   * Multiple calls fired synchronously (e.g. from a forEach over an agent response)
   * share one groupId so ArtifactPanel can group them into a single card.
   * The group is cleared asynchronously via setTimeout(0) so the next independent
   * user click starts a fresh group.
   *
   * Deps: openPanel (show panel after adding the item),
   *       setSelectedArtifacts (remove duplicate then append).
   */
  const openAnalysisOutput = useCallback(
    (artifact: ArtifactProto) => {
      if (!artifact.analysis_output) {
        showToast("Missing analysis_output data", "", "error");
        return;
      }

      if (!pendingAnalysisGroupRef.current) {
        const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const timer = setTimeout(() => {
          pendingAnalysisGroupRef.current = null;
        }, 0);
        pendingAnalysisGroupRef.current = { groupId, timer };
      }
      const analysisGroupId = pendingAnalysisGroupRef.current.groupId;

      const incomingOutputId = artifact.analysis_output.header?.id;

      orderCounter.current++;
      const orderIndex = orderCounter.current;
      // Single updater call: duplicate detection and append are atomic.
      // Running dedup inside the updater (not before it) prevents the stale-index
      // bug where batched synchronous calls all read the same ref and remove the
      // wrong items from the already-modified prev state.
      setSelectedArtifacts(prev => {
        const dupIdx = incomingOutputId
          ? prev.findIndex(
              item =>
                getArtifactType(item.artifact!) === ArtifactType.ANALYSIS_OUTPUT &&
                item.artifact?.analysis_output?.header?.id === incomingOutputId
            )
          : -1;
        const filtered = dupIdx !== -1 ? prev.filter((_, idx) => idx !== dupIdx) : prev;
        return [...filtered, { artifact, orderIndex, loading: false, analysisGroupId }];
      });
      setActivateArtifact(prev => ({
        orderIndex,
        tick: (prev?.tick ?? 0) + 1,
      }));
      openPanel();
    },
    [openPanel, setSelectedArtifacts]
  );

  /**
   * Open a FILE_ARTIFACT artifact.
   * If pandas_dataframe.data_json is already populated: adds to panel immediately.
   * If not (e.g. on chat reload where data is lazy-loaded from disk): opens the
   * panel, fires populateArtifact to hydrate from disk, then upserts the result.
   *
   * Deps: hydrateArtifact, openPanel, upsertSelectedItem — all stable useCallbacks.
   */
  const openFileArtifact = useCallback(
    async (artifact: ArtifactProto, chatId?: string) => {
      if (!artifact.file_artifact) {
        showToast("Missing file artifact data", "", "error");
        return;
      }
      const artifactId = artifact.file_artifact.header?.id;
      const existingIdx = findInPanel(ArtifactType.FILE_ARTIFACT, artifactId);
      if (existingIdx !== -1) {
        const existing = selectedArtifactsRef.current[existingIdx];
        setActivateArtifact(prev => ({ orderIndex: existing.orderIndex, tick: (prev?.tick ?? 0) + 1 }));
        openPanel();
        return;
      }
      openPanel();
      const isPopulated = !!artifact.file_artifact.pandas_dataframe?.data_json;
      if (isPopulated) {
        const orderIndex = upsertSelectedItem({ artifact, loading: false }, artifactId);
        setActivateArtifact(prev => ({ orderIndex, tick: (prev?.tick ?? 0) + 1 }));
        return;
      }
      setIsLoadingArtifact(true);
      try {
        const hydrated = await hydrateArtifact(artifact, chatId);
        const finalArtifact = hydrated ?? artifact;
        const orderIndex = upsertSelectedItem({ artifact: finalArtifact, loading: false }, artifactId);
        setActivateArtifact(prev => ({ orderIndex, tick: (prev?.tick ?? 0) + 1 }));
      } finally {
        setIsLoadingArtifact(false);
      }
    },
    [hydrateArtifact, openPanel, upsertSelectedItem]
  );

  /**
   * Open a WORKFLOW_RUN artifact.
   * Nested FileArtifacts are hydrated via a single WORKFLOW_RUN populate (mirrors DFA).
   * Re-clicks with subResourceName update the active tab/pill without re-fetching.
   *
   * Deps: hydrateArtifact, openPanel, upsertSelectedItem — all stable useCallbacks.
   */
  const openWorkflowRun = useCallback(
    async (artifact: ArtifactProto, chatId?: string, activeTab?: string, subResourceName?: string) => {
      if (!artifact.workflow_run) {
        showToast("Missing workflow_run data", "", "error");
        return;
      }

      const artifactId = getArtifactHeaderId(artifact);
      const workflowRunView = buildWorkflowRunViewState(artifact, activeTab, subResourceName);

      const existingPanelIdx = findInPanel(ArtifactType.WORKFLOW_RUN, artifactId);
      const panelArtifact =
        existingPanelIdx >= 0 ? (selectedArtifactsRef.current[existingPanelIdx]!.artifact ?? artifact) : artifact;

      const panelNeedsHydration = workflowRunNeedsHydration(panelArtifact);
      const incomingNeedsHydration = workflowRunNeedsHydration(artifact);
      const shouldPopulate = panelNeedsHydration && incomingNeedsHydration;

      openPanel();

      if (!shouldPopulate) {
        const artifactToShow = panelArtifact;
        updateArtifact(artifactToShow);
        const activatedOrderIndex = upsertSelectedItem(
          { artifact: artifactToShow, loading: false, workflowRunView },
          artifactId
        );
        setActivateArtifact(prev => ({
          orderIndex: activatedOrderIndex,
          tick: (prev?.tick ?? 0) + 1,
        }));
        return;
      }

      setIsLoadingArtifact(true);
      try {
        const hydratedArtifact = await hydrateArtifact(artifact, chatId);
        const artifactToShow = hydratedArtifact ?? artifact;
        const activatedOrderIndex = upsertSelectedItem(
          { artifact: artifactToShow, loading: false, workflowRunView },
          artifactId
        );
        setActivateArtifact(prev => ({
          orderIndex: activatedOrderIndex,
          tick: (prev?.tick ?? 0) + 1,
        }));
      } finally {
        setIsLoadingArtifact(false);
      }
    },
    [hydrateArtifact, openPanel, updateArtifact, upsertSelectedItem]
  );

  /**
   * Open a WORKFLOW_RUN_SEARCH artifact.
   * No hydration needed — search results are fully populated on arrival.
   * Re-clicks bring the tab to the front (same pattern as SCENARIO_JOB).
   *
   * Deps: openPanel (show panel immediately),
   *       setSelectedArtifacts (atomic remove + append in one update).
   */
  const openWorkflowRunSearch = useCallback(
    (artifact: ArtifactProto) => {
      if (!artifact.workflow_run_search) {
        showToast("Missing workflow_run_search data", "", "error");
        return;
      }
      openPanel();
      const incomingSearchId = artifact.workflow_run_search.header?.id;
      orderCounter.current++;
      const orderIndex = orderCounter.current;
      setSelectedArtifacts(prev => {
        const dupIdx = incomingSearchId
          ? prev.findIndex(
              item =>
                getArtifactType(item.artifact!) === ArtifactType.WORKFLOW_RUN_SEARCH &&
                item.artifact?.workflow_run_search?.header?.id === incomingSearchId
            )
          : -1;
        const filtered = dupIdx !== -1 ? prev.filter((_, i) => i !== dupIdx) : prev;
        return [...filtered, { artifact, orderIndex, loading: false }];
      });
      setActivateArtifact(prev => ({
        orderIndex,
        tick: (prev?.tick ?? 0) + 1,
      }));
    },
    [openPanel, setSelectedArtifacts]
  );

  /**
   * Unified entry point — routes to the correct typed handler based on artifact_type.
   * Equivalent to the former 310-line `handleArtifactClick` in ChatPage.
   *
   * KG_DATAFRAME and DATAFRAME_ANALYSIS: if the tab is already open, activates it
   * in-place (no re-fetch). Otherwise delegates to the typed handler.
   * SCENARIO_JOB and ANALYSIS_OUTPUT: always delegate (they have their own re-click
   * semantics — remove + re-add).
   *
   * Deps: openPanel (called directly in the re-click shortcut),
   *       openKGDataFrame, openDataFrameAnalysis, openScenarioJob, openAnalysisOutput,
   *       openFileArtifact — all stable useCallbacks, so this callback is also stable.
   */
  const openArtifact = useCallback(
    (artifact: ArtifactProto, chatId?: string, activeTab?: string, subResourceName?: string) => {
      const type = getArtifactType(artifact);

      // For KG_DATAFRAME, DATAFRAME_ANALYSIS, and WORKFLOW_RUN, a re-click on an already-open tab
      // activates it without re-fetching (unless a specific sub-resource view is requested).
      if (
        type === ArtifactType.KG_DATAFRAME ||
        type === ArtifactType.DATAFRAME_ANALYSIS ||
        type === ArtifactType.WORKFLOW_RUN
      ) {
        const incomingId = getArtifactHeaderId(artifact);
        const existingIdx = findInPanel(type, incomingId);
        if (existingIdx !== -1) {
          if (type === ArtifactType.DATAFRAME_ANALYSIS && (activeTab !== undefined || subResourceName !== undefined)) {
            return openDataFrameAnalysis(artifact, chatId, activeTab, subResourceName);
          }
          if (type === ArtifactType.WORKFLOW_RUN && (activeTab !== undefined || subResourceName !== undefined)) {
            return openWorkflowRun(artifact, chatId, activeTab, subResourceName);
          }
          const existing = selectedArtifactsRef.current[existingIdx];
          setActivateArtifact(prev => ({
            orderIndex: existing.orderIndex,
            tick: (prev?.tick ?? 0) + 1,
          }));
          openPanel();
          return;
        }
      }

      switch (type) {
        case ArtifactType.KG_DATAFRAME:
          return openKGDataFrame(artifact);
        case ArtifactType.DATAFRAME_ANALYSIS:
          return openDataFrameAnalysis(artifact, chatId, activeTab, subResourceName);
        case ArtifactType.SCENARIO_JOB:
          return openScenarioJob(artifact);
        case ArtifactType.ANALYSIS_OUTPUT:
          return openAnalysisOutput(artifact);
        case ArtifactType.FILE_ARTIFACT:
          return openFileArtifact(artifact, chatId);
        case ArtifactType.WORKFLOW_RUN:
          return openWorkflowRun(artifact, chatId, activeTab, subResourceName);
        case ArtifactType.WORKFLOW_RUN_SEARCH:
          return openWorkflowRunSearch(artifact);
        default:
          showToast("Artifact type not supported yet", "", "error");
      }
    },
    [
      openPanel,
      openKGDataFrame,
      openDataFrameAnalysis,
      openScenarioJob,
      openAnalysisOutput,
      openFileArtifact,
      openWorkflowRun,
      openWorkflowRunSearch,
    ]
  );

  /**
   * Hydrate an artifact in place without opening a new panel tab.
   * Used by the Inputs tab to load parent dataframe data inline.
   * Skips the network call if the artifact is already populated.
   *
   * Deps: hydrateArtifact (shared fetch+cache helper).
   */
  const populateSourceArtifact = useCallback(
    async (artifact: ArtifactProto, chatId?: string) => {
      const type = getArtifactType(artifact);
      if (type === ArtifactType.KG_DATAFRAME && artifact.kg_dataframe?.pandas_dataframe?.data_json) return;
      if (
        type === ArtifactType.DATAFRAME_ANALYSIS &&
        artifact.dataframe_analysis?.pandas_dataframes.some(df => !!df.data_json)
      )
        return;
      if (type === ArtifactType.FILE_ARTIFACT && artifact.file_artifact?.pandas_dataframe?.data_json) return;
      if (type === ArtifactType.WORKFLOW_RUN && !workflowRunNeedsHydration(artifact)) return;
      await hydrateArtifact(artifact, chatId);
    },
    [hydrateArtifact]
  );

  // ── Panel management ───────────────────────────────────────────────────────

  /**
   * Hide the panel and reset its expanded state.
   * Deps: none — only stable React setters.
   */
  const closePanel = useCallback(() => {
    setIsVisible(false);
    setIsExpanded(false);
  }, []);

  /**
   * Clears all tabs and resets the order counter.
   * Sets both state and the ref directly (no functional update needed since the
   * next value is always an empty array, regardless of prev).
   * Resetting orderCounter ensures the first tab of the next session starts at 1.
   *
   * Deps: none — touches only stable React setters and the ref.
   */
  const clearAll = useCallback(() => {
    orderCounter.current = 0;
    selectedArtifactsRef.current = [];
    setSelectedArtifactsInternal([]);
  }, []);

  /**
   * Remove a specific artifact from the panel tab list by object reference.
   * Deps: setSelectedArtifacts — the only call made inside.
   */
  const removeArtifact = useCallback(
    (artifactToRemove: ArtifactProto) => {
      setSelectedArtifacts(prev => prev.filter(item => item.artifact !== artifactToRemove));
    },
    [setSelectedArtifacts]
  );

  /**
   * Persist the view state (active tab + selected df/chart index) for a specific
   * panel tab, identified by orderIndex. Called by ArtifactPanel when the user
   * switches tabs or selects a different dataframe/chart inside a DataFrameAnalysis card.
   *
   * Deps: setSelectedArtifacts — the only call made inside.
   */
  const updateViewState = useCallback(
    (
      orderIndex: number,
      view: { activeTab: string; selectedResultPillName: string; selectedInputPillName?: string }
    ) => {
      setSelectedArtifacts(prev =>
        prev.map(item =>
          item.orderIndex === orderIndex
            ? {
                ...item,
                dataframeAnalysisView: {
                  ...item.dataframeAnalysisView,
                  activeTab: view.activeTab,
                  selectedItemName: view.selectedResultPillName,
                  selectedInputPillName: view.selectedInputPillName,
                },
              }
            : item
        )
      );
    },
    [setSelectedArtifacts]
  );

  /**
   * Persists tab + pill selection changes for a WORKFLOW_RUN artifact so that the
   * view is restored when the user navigates back to this tab.
   *
   * Deps: setSelectedArtifacts — the only call made inside.
   */
  const updateWorkflowRunViewState = useCallback(
    (
      orderIndex: number,
      view: { activeTab: string; selectedOutputPillName: string; selectedInputPillName?: string }
    ) => {
      setSelectedArtifacts(prev =>
        prev.map(item =>
          item.orderIndex === orderIndex
            ? {
                ...item,
                workflowRunView: {
                  ...item.workflowRunView,
                  activeTab: view.activeTab,
                  selectedOutputPillName: view.selectedOutputPillName,
                  selectedInputPillName: view.selectedInputPillName,
                },
              }
            : item
        )
      );
    },
    [setSelectedArtifacts]
  );

  /**
   * Patch all panel tabs that reference a specific SCENARIO_JOB with an updated job proto.
   * Called by ChatPage after a workflow is saved, so the panel shows the persisted version
   * without needing a full re-fetch.
   *
   * Deps: setSelectedArtifacts — the only call made inside.
   */
  const updateJobInPanel = useCallback(
    (job: ScheduledJobProto) => {
      if (!job.header?.id) return;
      const jobId = job.header.id;
      setSelectedArtifacts(prev =>
        prev.map(item =>
          getArtifactType(item.artifact!) === ArtifactType.SCENARIO_JOB &&
          item.artifact?.scenario_job?.header?.id === jobId
            ? { ...item, artifact: { ...item.artifact!, scenario_job: job } }
            : item
        )
      );
    },
    [setSelectedArtifacts]
  );

  return {
    // State
    selectedArtifacts,
    isVisible,
    isExpanded,
    isLoadingArtifact,
    activateArtifact,
    // Setters for state managed externally (e.g. ChatPage toggle expand)
    setIsVisible,
    setIsExpanded,
    // Actions
    openArtifact,
    populateSourceArtifact,
    closePanel,
    clearAll,
    removeArtifact,
    updateViewState,
    updateJobInPanel,
    openWorkflowRun,
    updateWorkflowRunViewState,
  };
}
